/**
 * Plantillas fijas de los mosaicos del modo Dashboard: de (tipo, encoding,
 * filas) a la configuración de ApexCharts o a los datos de un KPI. Módulo
 * PURO (sin React ni DOM, salvo readThemeColors): el mismo código podrá
 * dibujar en el servidor para imágenes/PDF (docs/tablero-ia-y-tablero-vivo.md,
 * sección 3.7). Sin llamadas a la IA: dibujar un mosaico es determinista.
 *
 * Tipos: kpi | line | bar | top | donut | table. Encoding por tipo:
 *   kpi {value, previous?}; line/bar/top {x, y, series?}; donut {label, value};
 *   table {columns?, formats?: {columna: formato}}; los demás aceptan
 *   format (number|currency|percent) y unit. En una tabla cada columna tiene
 *   su propio formato (por defecto número): uno global pondría "$" a todo.
 */

// Paleta de respaldo (tema oscuro) si no se pueden leer los tokens.
const FALLBACK_COLORS = {
  mode: 'dark',
  text: '#e2e8f0',
  muted: '#94a3b8',
  grid: 'rgba(148, 163, 184, 0.16)',
  series: ['#38bdf8', '#4ade80', '#fbbf24', '#6366f1', '#fb923c', '#f87171', '#60a5fa']
};
const SERIES_TOKENS = ['--wb-accent', '--wb-success', '--wb-warning', '--wb-indigo', '--wb-orange', '--wb-danger', '--wb-blue'];
const MAX_CATEGORIES = 60;
const TOP_N = 10;

/** ApexCharts necesita colores reales (no var()): se leen del tema activo. */
export const readThemeColors = () => {
  try {
    const styles = window.getComputedStyle(document.documentElement);
    const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
    return {
      mode: read('--wb-mode', FALLBACK_COLORS.mode),
      text: read('--wb-text', FALLBACK_COLORS.text),
      muted: read('--wb-muted', FALLBACK_COLORS.muted),
      grid: read('--wb-border-16', FALLBACK_COLORS.grid),
      series: SERIES_TOKENS.map((token, i) => read(token, FALLBACK_COLORS.series[i]))
    };
  } catch (error) {
    return FALLBACK_COLORS;
  }
};

/** Athena devuelve todo como texto: "1234.5" -> 1234.5. Lo no numérico, null. */
export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatValue = (value, format, unit) => {
  const number = toNumber(value);
  if (number === null) return value === null || value === undefined || value === '' ? '—' : String(value);
  let text;
  if (format === 'percent') {
    // 0.25 -> 25 %; 25 -> 25 % (los motores devuelven ambas formas)
    const pct = Math.abs(number) <= 1 ? number * 100 : number;
    text = `${pct.toLocaleString('es-MX', { maximumFractionDigits: 1 })} %`;
  } else if (format === 'currency') {
    text = `$${number.toLocaleString('es-MX', { maximumFractionDigits: Math.abs(number) >= 100 ? 0 : 2 })}`;
  } else {
    text = number.toLocaleString('es-MX', { maximumFractionDigits: 2 });
  }
  return unit ? `${text} ${unit}` : text;
};

const records = (tile) => (tile.result && tile.result.records) || [];

/** KPI: valor, valor de comparación y variación porcentual. */
export const buildKpi = (tile) => {
  const row = records(tile)[0] || {};
  const { value, previous, format, unit } = tile.encoding || {};
  const current = toNumber(row[value]);
  const before = previous ? toNumber(row[previous]) : null;
  let variation = null;
  let variationText = null;
  if (format === 'percent' && current !== null && before !== null) {
    // Un porcentaje se compara en puntos porcentuales, no en % relativo.
    const toPct = (n) => (Math.abs(n) <= 1 ? n * 100 : n);
    variation = toPct(current) - toPct(before);
    variationText = `${variation > 0 ? '+' : ''}${variation.toLocaleString('es-MX', { maximumFractionDigits: 1 })} pp`;
  } else if (current !== null && before) {
    variation = (current - before) / Math.abs(before);
    variationText = `${variation > 0 ? '+' : ''}${(variation * 100).toLocaleString('es-MX', { maximumFractionDigits: 1 })} %`;
  }
  return {
    value: formatValue(row[value], format, unit),
    previous: before !== null ? formatValue(before, format, unit) : null,
    variation,
    variationText,
    trend: variation === null ? 'flat' : variation > 0 ? 'up' : variation < 0 ? 'down' : 'flat'
  };
};

/** Categorías y series (con o sin columna de serie), en el orden de las filas. */
export const pivot = (rows, x, y, seriesKey) => {
  const categories = [];
  rows.forEach((row) => {
    const category = row[x] === null || row[x] === undefined ? '—' : String(row[x]);
    if (!categories.includes(category)) categories.push(category);
  });
  const limited = categories.slice(0, MAX_CATEGORIES);
  if (!seriesKey) {
    const values = Object.fromEntries(rows.map((row) => [String(row[x] ?? '—'), toNumber(row[y])]));
    return { categories: limited, series: [{ name: y, data: limited.map((c) => values[c] ?? null) }] };
  }
  const names = [];
  rows.forEach((row) => {
    const name = String(row[seriesKey] ?? '—');
    if (!names.includes(name)) names.push(name);
  });
  const lookup = {};
  rows.forEach((row) => { lookup[`${row[seriesKey] ?? '—'}\u0000${row[x] ?? '—'}`] = toNumber(row[y]); });
  return {
    categories: limited,
    series: names.map((name) => ({ name, data: limited.map((c) => lookup[`${name}\u0000${c}`] ?? null) }))
  };
};

const baseOptions = (colors, format, unit) => ({
  chart: { toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent', fontFamily: 'inherit' },
  colors: colors.series,
  dataLabels: { enabled: false },
  grid: { borderColor: colors.grid },
  legend: { labels: { colors: colors.text } },
  tooltip: { theme: colors.mode === 'light' ? 'light' : 'dark', y: { formatter: (v) => formatValue(v, format, unit) } },
  xaxis: { labels: { style: { colors: colors.muted } }, axisBorder: { color: colors.grid }, axisTicks: { color: colors.grid } },
  yaxis: { labels: { style: { colors: colors.muted }, formatter: (v) => formatValue(v, format, unit) } }
});

/** Configuración de ApexCharts para line/bar/top/donut: {type, options, series}. */
export const buildChart = (tile, colors = FALLBACK_COLORS) => {
  const { type } = tile;
  const encoding = tile.encoding || {};
  const { format, unit } = encoding;
  const rows = records(tile);
  const options = baseOptions(colors, format, unit);

  if (type === 'donut') {
    const slices = rows.slice(0, 8);
    return {
      type: 'donut',
      series: slices.map((row) => toNumber(row[encoding.value]) ?? 0),
      options: {
        ...options,
        labels: slices.map((row) => String(row[encoding.label] ?? '—')),
        stroke: { width: 0 },
        legend: { ...options.legend, position: 'bottom' },
        plotOptions: { pie: { donut: { labels: { show: false } } } }
      }
    };
  }

  if (type === 'top') {
    const sorted = [...rows]
      .sort((a, b) => (toNumber(b[encoding.y]) ?? -Infinity) - (toNumber(a[encoding.y]) ?? -Infinity))
      .slice(0, TOP_N);
    const { categories, series } = pivot(sorted, encoding.x, encoding.y, null);
    return {
      type: 'bar',
      series,
      options: {
        ...options,
        plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
        xaxis: { ...options.xaxis, categories, labels: { ...options.xaxis.labels, formatter: (v) => formatValue(v, format, unit) } },
        yaxis: { labels: { style: { colors: colors.muted } } }
      }
    };
  }

  const { categories, series } = pivot(rows, encoding.x, encoding.y, encoding.series);
  return {
    type: type === 'line' ? 'area' : 'bar',
    series,
    options: {
      ...options,
      xaxis: { ...options.xaxis, categories },
      ...(type === 'line'
        ? { stroke: { curve: 'smooth', width: 2 }, fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.02 } } }
        : { plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } } }),
      legend: { ...options.legend, show: series.length > 1 }
    }
  };
};

/** Columnas de una tabla de detalle: las del encoding o las del resultado. */
export const tableColumns = (tile) => {
  const wanted = (tile.encoding && tile.encoding.columns) || [];
  const available = (tile.result && tile.result.columns) || Object.keys(records(tile)[0] || {});
  const chosen = wanted.filter((column) => available.includes(column));
  return chosen.length ? chosen : available;
};

/** Formato de una columna de tabla: el de encoding.formats o número. */
export const columnFormat = (tile, column) => ((tile.encoding && tile.encoding.formats) || {})[column] || 'number';
